import { useEffect, useRef, useState } from 'react';
import { formatUkPhone, normaliseUkMobile } from '@tikkas/shared';
import { COIN, Svg } from '../art/icons.js';
import { api } from '../lib/api.js';
import { useNav } from '../lib/router.js';
import { rewardPoints, useStore, welcomeToCome } from '../lib/store.js';
import { num } from '../lib/format.js';
import { Scroll, TopBar } from '../components/ui.js';
import type { Customer } from '../lib/types.js';
import { APP_BUILD, APP_ENV, setSessionToken } from '../lib/platform.js';
import { LegalLinks } from './Legal.js';
import { WalletButtons, walletsHere, walletsIssued } from '../components/Wallet.js';

import { normaliseClaim } from '../lib/claims.js';
import { claimMessage, joinBenefits, welcomeLine } from '../lib/joining.js';
import { Logo, logoUrlFor, useLook } from '../lib/look.js';
import { cardFace, CardLayers, CardLogo } from '../components/TikkasCard.js';
import { reduced } from '../lib/format.js';
import { parseGuestParam } from '../lib/guest.js';
import { markJustJoined } from '../lib/notifications.js';
type ClaimInfo = { status: 'open' | 'claimed' | 'expired' | 'void'; points: number; expiresAt: string; siteName: string };

/**
 * The mobile number, then the code by text (Twilio Verify), then Add to Wallet (CLAUDE.md §4). One heading, one field and
 * one button per step (Saif, 24 Oct 2026: as simple as it can be; no marketing tick boxes here, consent lives under Account ›
 * Notifications). A new member is asked for a first name once, after the code (the name sheet). One account per number.
 * Customers can browse before signing up. Welcome points arrive with the first purchase.
 */
/**
 * The join page's card (Saif, 24 Oct 2026): the Tikkas card at the top with the benefits of joining playing on its face one
 * line at a time, about 1.2 s each, looping gently (transform and opacity only); the name slot reads "Your name". Under
 * Reduce Motion or with the theme's motion off the same lines stand as a plain list. The lines come from the wording
 * settings with the numbers from the rewards settings (lib/joining.ts joinBenefits).
 */
function BenefitsCard() {
  const s = useStore();
  const { v, t } = useLook();
  const r = rewardPoints(s);
  const here = walletsHere(s);
  const lines = joinBenefits({ welcome: r.signup, card: s.config?.rewards.pointsPerPound.card ?? 5, credit: s.config?.rewards.pointsPerPound.credit ?? 10, side: r.side, main: r.main, wallet: here.includes('apple') ? 'Apple Wallet' : here.includes('google') ? 'Google Wallet' : null }, t);
  const still = !v.theme.motion || reduced();
  const face = cardFace(v.cardDesign, v.theme.motion, logoUrlFor(v), still);
  const [i, setI] = useState(0);
  const n = lines.length;
  const timer = useRef(0);
  useEffect(() => {
    if (still || n < 2) return;
    const tick = () => { setI((k) => (k + 1) % n); timer.current = window.setTimeout(tick, 1200 + 400); };
    timer.current = window.setTimeout(tick, 1200 + 400);
    return () => clearTimeout(timer.current);
  }, [still, n]);
  return (
    <section className={`${face.className} still joincard benefits ${still ? 'static' : ''}`} aria-label="Join the Rewards Club" {...face.attrs} style={face.style}>
      <CardLayers d={v.cardDesign} />
      <div className="bc-top"><CardLogo /><Svg html={COIN(26)} /></div>
      {still ? (
        <ul className="bc-benlist">{lines.map((l) => <li key={l}>{l}</li>)}</ul>
      ) : (
        <div className="bc-ben" aria-live="polite">
          {lines.map((l, k) => <span key={l} className={k === i ? 'on' : k === (i + n - 1) % n ? 'gone' : ''} aria-hidden={k !== i}>{l}</span>)}
        </div>
      )}
      <div className="bc-foot"><span className="bc-who"><span className="bc-name">Your name</span><span className="bc-kick">Rewards Club</span></span></div>
    </section>
  );
}

export function Join() {
  const s = useStore();
  const { t } = useLook();
  const { navigate, route, back } = useNav();
  const next = route.search.get('next') ?? '/';
  // From the counter QR or a receipt there is no `next`: after signing in, the page offers Add to Wallet.
  const fromQr = !route.search.get('next');
  const claimCode = normaliseClaim(route.search.get('r'));
  const [claimInfo, setClaimInfo] = useState<ClaimInfo | null>(null);
  useEffect(() => {
    if (claimCode) api<ClaimInfo>(`/api/claims/${encodeURIComponent(claimCode)}`).then(setClaimInfo).catch(() => setClaimInfo(null));
  }, [claimCode]);
  // From a guest order's nudge (docs/30): the name and mobile the guest gave are filled in, and the order joins the account on sign-in.
  const guestOrder = parseGuestParam(route.search.get('guest'));
  const [step, setStep] = useState<'phone' | 'code' | 'done'>('phone');
  // A guest's name (docs/30) travels with the sign-in so a new member isn't asked for it again; the mobile is filled in too.
  const guestName = (route.search.get('name') ?? '').trim().slice(0, 40);
  const [phone, setPhone] = useState(() => (route.search.get('phone') ?? '').replace(/^\+?44/, '').replace(/^0/, ''));
  const [code, setCode] = useState('');
  const [testCode, setTestCode] = useState<string | null>(null);
  // A friend's invite link (/join?ref=…, or ?code= from older links) arrives with their code filled in.
  const invited = route.search.get('ref') ?? route.search.get('code') ?? '';
  const [refCode, setRefCode] = useState(() => invited.trim().toUpperCase().slice(0, 30));
  const [refOpen, setRefOpen] = useState(() => !!invited);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cardStep = step === 'phone' && !(s.me && (claimCode || guestOrder)) && s.config?.auth.accountsOpen !== false;
  const start = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const n = normaliseUkMobile(phone);
    if (!n) return setErr('Enter a UK mobile number, for example 07700 900123');
    setBusy(true);
    try {
      const r = await api<{ channel: string; testCode?: string }>('/api/auth/start', { body: { phone: n } });
      setTestCode(r.testCode ?? null);
      setStep('code');
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not send the code');
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await api<{ isNew: boolean; customer: Customer; qrSecret: string; sessionToken?: string; claim?: { ok: boolean; pointsAdded?: number; message: string }; guestAttach?: { ok: boolean; message: string } }>('/api/auth/verify', { body: { phone: normaliseUkMobile(phone), code, ...(guestName ? { firstName: guestName } : {}), referralCode: refCode || undefined, ...(claimCode ? { claimCode } : {}), ...(guestOrder ? { guestOrder } : {}) } });
      // The app keeps its sign-in in the Keychain (the website uses the cookie the server just set).
      if (r.sessionToken) await setSessionToken(r.sessionToken);
      s.setMeFromSignIn({ customer: r.customer, qrSecret: r.qrSecret });
      if (refCode && !s.code) s.applyCode(refCode).catch(() => undefined);
      // A receipt's points (a bad claim never stops the sign-in; it just says why).
      if (r.claim) s.toast(r.claim.message);
      // The guest order is theirs now (docs/30): the phone forgets the guest link; the order page is the account's own.
      if (guestOrder) s.forgetGuestOrder(guestOrder.id);
      if (r.guestAttach) s.toast(r.guestAttach.message);
      if (fromQr && walletsHere(s).length) setStep('done');
      else navigate(next, { root: next === '/', replace: true });
      // A new member is asked for a first name by the name sheet, which carries the welcome (a toast would cover its Save
      // button); one who already has a name is welcomed in a toast. The welcome points come with the first order.
      const welcome = welcomeLine(r.customer.welcomeGiven === false ? rewardPoints(s).signup : 0);
      // A new member's first Home then asks about notifications, once the name sheet is done (or straight away without one).
      if (r.isNew && !r.customer.firstName) setTimeout(() => s.openSheet({ kind: 'name', welcome: true }), 400);
      else if (r.isNew) markJustJoined();
      else if (r.isNew && !r.claim && !r.guestAttach) s.toast(`Welcome to the Rewards Club, ${r.customer.firstName}.${welcome ? ` ${welcome}.` : ''}`);
      else if (!r.claim && !r.guestAttach) s.toast(`Welcome back${r.customer.firstName ? `, ${r.customer.firstName}` : ''}`);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'That code did not work');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="" onBack={back} />
      {/* The phone step opens with the card (Saif, 24 Oct 2026); the other steps keep a heading. */}
      {cardStep ? <BenefitsCard /> : <div className="col" style={{ gap: 10, alignItems: 'center', textAlign: 'center' }}>
        <Logo className="logo" style={{ height: 38 }} />
        <h1 className="display" style={{ fontSize: 30 }}>{step === 'done' ? "You're in" : s.me && claimCode ? 'Claim your points' : s.me && guestOrder ? 'Add your order' : step === 'phone' ? 'Sign in or join' : 'Check your texts'}</h1>
        {step === 'done' ? <p className="soft" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, maxWidth: 300 }}>Add your Tikkas card to your phone: show it at the till to collect points and pay with credit.</p>
          : s.me && claimCode ? <p className="soft" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, maxWidth: 300 }}>Add the points from your receipt to your account.</p>
          : step === 'code' ? <p className="soft" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, maxWidth: 300 }}>We sent a 6-digit code to {formatUkPhone(normaliseUkMobile(phone) ?? phone)}.</p> : null}
      </div>}
      {s.config?.auth.accountsOpen === false && (
        <div className="card" role="status" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <b style={{ fontSize: 16 }}>Accounts open soon</b>
          <span className="small muted">{(s.config.auth.closedMessage ?? 'You can browse the menu in the meantime.').replace(/^Accounts open soon\.\s*/, '')} {rewardPoints(s).signup > 0 ? ` Join the day we open and ${welcomeLine(rewardPoints(s).signup)}.` : ''}</span>
          <button type="button" className="yellow" style={{ marginTop: 8 }} onClick={() => navigate('/menu', { root: true })}>See the menu</button>
        </div>
      )}
      {claimInfo && step !== 'done' && <ClaimBanner info={claimInfo} />}
      {step === 'done' && welcomeToCome(s) > 0 && <div className="wticket"><Svg html={COIN(30)} /><span className="col" style={{ gap: 1 }}><b>{num(welcomeToCome(s))} welcome points on their way</b><span>They land with your first order, in the app or in the shop.</span></span></div>}
      {step === 'done' && <JoinDone onContinue={() => navigate(next, { root: next === '/', replace: true })} />}
      {step !== 'done' && s.me && claimCode && <ClaimNow code={claimCode} info={claimInfo} onDone={() => (walletsHere(s).length ? setStep('done') : navigate('/card', { root: true, replace: true }))} />}
      {step !== 'done' && s.me && !claimCode && guestOrder && <AttachNow order={guestOrder} onDone={() => navigate(next, { root: next === '/', replace: true })} />}
      {step === 'done' || (s.me && (claimCode || guestOrder)) ? null : <>
      {s.config?.auth.accountsOpen === false ? null : step === 'phone' ? (
        <form className="col" style={{ gap: 14 }} onSubmit={start}>
          <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }} htmlFor="phone">Mobile number
            <span className="phonein"><span>+44</span><input id="phone" className="txt-in" type="tel" inputMode="tel" placeholder="7700 900123" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></span></label>
          {refOpen ? <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>Got a code? (friend, office or creator)<input className="txt-in" type="text" autoComplete="off" placeholder="e.g. ACME5" value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} /></label> : null}
          {err && <p className="err" role="alert">{err}</p>}
          <button type="submit" className="yellow" disabled={busy}>{busy ? 'Sending…' : t('button.continue')}</button>
          <LegalLinks lead="By continuing you agree to our" docs={['terms', 'rewards', 'privacy']} />
          {walletsIssued(s) && <button type="button" className="link" style={{ alignSelf: 'center' }} onClick={() => navigate('/card/back')}>Lost your card from your wallet? Get it back</button>}
        </form>
      ) : (
        <form className="col" style={{ gap: 14 }} onSubmit={verify}>
          {/* Off production the server shows the code when it sent no text (no Twilio on staging yet). The TestFlight build keeps
              this so testers can sign in; it never appears in a production build, where the server always texts. */}
          {testCode && APP_ENV !== 'production' && (APP_BUILD
            ? <div className="testtools"><span>Test sign-in: your code is <b style={{ fontSize: 20, letterSpacing: '.2em' }}>{testCode}</b></span></div>
            : <div className="testtools"><span className="staging">Staging test mode · no text was sent</span><span>Your code is <b style={{ fontSize: 20, letterSpacing: '.2em' }}>{testCode}</b></span><span className="small muted">Real texts start once Twilio is set up and your number is on the staging list.</span></div>)}
          <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }} htmlFor="code">6-digit code
            <input id="code" className="txt-in" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus style={{ letterSpacing: '.3em', fontSize: 22, textAlign: 'center' }} /></label>
          {err && <p className="err" role="alert">{err}</p>}
          <button type="submit" className="yellow" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Sign in'}</button>
          <button type="button" className="link" style={{ alignSelf: 'center' }} onClick={() => { setStep('phone'); setCode(''); setErr(null); }}>Wrong number? Go back</button>
        </form>
      )}
      </>}
    </Scroll>
  );
}

/** What a receipt's code is worth, or why it can't be claimed (docs/23 §3.5). Never names the customer or the items. */
function ClaimBanner({ info }: { info: ClaimInfo }) {
  const text = info.status === 'open' ? `${num(info.points)} points from your receipt are waiting. Join or sign in and they're yours.`
    : info.status === 'claimed' ? 'The points on that receipt have already been claimed.'
    : info.status === 'expired' ? 'That receipt is too old to claim, but you can still join.'
    : 'That sale was refunded, so it has no points to claim.';
  return <div className="wticket"><Svg html={COIN(30)} /><span className="col" style={{ gap: 1 }}><b>{info.status === 'open' ? `${num(info.points)} points to claim` : 'Your receipt'}</b><span>{text}</span></span></div>;
}

/** Signed in already, from a guest order's nudge (docs/30): one tap puts the order on this account. */
function AttachNow({ order, onDone }: { order: { id: string; token: string }; onDone: () => void }) {
  const s = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ pointsAdded: number; pointsPending: number; order: { number: number } }>('/api/me/guest-orders/attach', { body: order });
      s.forgetGuestOrder(order.id);
      s.toast(r.pointsAdded > 0 ? `Order #${r.order.number} added · +${num(r.pointsAdded)} points` : r.pointsPending > 0 ? `Order #${r.order.number} added · ${num(r.pointsPending)} points when you collect` : `Order #${r.order.number} added to your account`);
      await s.refreshMe();
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not add that order');
      setBusy(false);
    }
  };
  return (
    <div className="col" style={{ gap: 10 }}>
      <p className="soft" style={{ margin: 0, textAlign: 'center', fontSize: 14 }}>Put the order you placed as a guest on this account, with its points.</p>
      {err && <p className="err" role="alert" style={{ margin: 0 }}>{err}</p>}
      <button type="button" className="yellow" disabled={busy} onClick={go}>{busy ? 'One moment…' : 'Add the order to my account'}</button>
      <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>Signed in as {s.me?.customer.phoneDisplay}</p>
    </div>
  );
}

/** Signed in already: one tap adds the receipt's points. */
function ClaimNow({ code, info, onDone }: { code: string; info: ClaimInfo | null; onDone: () => void }) {
  const s = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ ok: boolean; pointsAdded: number; receiptPoints?: number; welcomePoints?: number }>('/api/me/claims', { body: { code } });
      s.toast(claimMessage(r));
      await s.refreshMe();
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not claim those points');
      setBusy(false);
    }
  };
  if (info && info.status !== 'open') return null;
  return (
    <div className="col" style={{ gap: 10 }}>
      {err && <p className="err" role="alert" style={{ margin: 0 }}>{err}</p>}
      <button type="button" className="yellow" disabled={busy} onClick={go}>{busy ? 'One moment…' : info ? `Add ${num(info.points)} points to my account` : 'Add the points to my account'}</button>
      <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>Receipt {code} · signed in as {s.me?.customer.phoneDisplay}</p>
    </div>
  );
}

/** After joining from the counter QR or a receipt: the card goes in the phone's wallet, or carry on. */
function JoinDone({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="col" style={{ gap: 12 }}>
      <WalletButtons />
      <button type="button" className="plain" onClick={onContinue}>Not now, open the app</button>
    </div>
  );
}
